import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type Company from 'garaje/models/company';
import type FlowQuestionnaire from 'garaje/models/flow-questionnaire';
import type Location from 'garaje/models/location';
import type QuestionnaireSection from 'garaje/models/questionnaire-section';
import type { LocaleKey } from 'garaje/utils/locale-options';
import { createLocalizedProxy, type CustomTranslations } from 'garaje/utils/translations';
import { TrackedObject } from 'tracked-built-ins';

const TRANSLATABLE_ATTRIBUTES = ['title'] as const;
type TranslatableAttributes = (typeof TRANSLATABLE_ATTRIBUTES)[number];

/**
 * Questionnaires are used to collect information from visitors as they sign in.
 * They may be created at either a location or company level; location-level questionnaires can only be assigned
 * to flows at that location, while company-level questionnaires can be assigned to any flow at any location
 * (including global flows).
 *
 * The Flow-Questionnaire relationship is many-to-many.
 *
 * Every questionnaire has one or more sections (`QuestionnaireSection` models).
 * Each section has one or more questions (`QuestionnaireField` models),
 * which in turn are associated with multiple `QuestionnaireFieldOption` to define the choices.
 * (Only a subset of sign in field types (single selection and multiple selection) are used for questionnaires.)
 */

export default class Questionnaire extends Model {
  // "Global" questionnaires are associated with a company, and can be used at any location
  @belongsTo('company', { async: false, inverse: null })
  declare company: Company | null;

  /**
   * intermediate relationship to enable a many-to-many use case between `Flow` and `Questionnaire`
   */
  @hasMany('flow-questionnaire', { async: false, inverse: 'questionnaire' })
  declare flowQuestionnaires: FlowQuestionnaire[];

  // Questionnaires associated with a single location can only be used at that location
  @belongsTo('location', { async: false, inverse: null })
  declare location: Location | null;

  @hasMany('questionnaire-section', { async: false, inverse: 'questionnaire' })
  declare questionnaireSections: QuestionnaireSection[];

  @attr('boolean') declare active: boolean;
  @attr('deeply-tracked-object', { defaultValue: () => new TrackedObject() })
  declare customTranslations: CustomTranslations<TranslatableAttributes>;
  @attr('boolean', { defaultValue: true }) declare forceRetakeOnRevision: boolean;
  @attr('boolean', { defaultValue: true }) declare requireAllCorrect: boolean;
  @attr('number', { defaultValue: 100 }) declare passingScore: number;
  @attr('string') declare title: string;
  @attr('number', { defaultValue: 30 }) declare validityDays: number | null;

  @attr('date') declare updatedAt: Date;

  /**
   * Whether this questionnaire is "global" or not. A global questionnaire is one that is only associated with
   * a company, not a location.
   */
  get isGlobal(): boolean {
    if (this.isDestroying || this.isDestroyed) return false;
    // @ts-expect-error - DefinitelyTyped types for `belongsTo` don't allow this (but it should, and it works, apart from the TS error)
    return !this.belongsTo('location').id();
  }

  forLocale(selectedLocale: LocaleKey, defaultLocale: LocaleKey): this {
    if (selectedLocale !== defaultLocale) {
      return createLocalizedProxy(this, <string[]>(<unknown>TRANSLATABLE_ATTRIBUTES), selectedLocale);
    }
    return this;
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    questionnaire: Questionnaire;
  }
}
